import { ChangeEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { faMapLocation } from '@fortawesome/pro-solid-svg-icons/faMapLocation';
import toast from 'react-hot-toast';
import Cookies from 'universal-cookie';

import { Alert } from 'us-react-components/dist/Alert/Alert';
import { states } from 'us-react-components/dist/constants/States';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { InfoCard } from 'us-react-components/dist/InfoCard/InfoCard';
import { Input } from 'us-react-components/dist/Input/Input';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { axios, CityService, ParentService, SavedSearchService,
  JobAvailabilityType, ValidationService, AnonymousUserService } from 'us-web-services';

import { Storage } from '../../../constants';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';
import { createSavedSearch } from '../../../utils';

export const Location = observer((): JSX.Element => {
  const {
    stores: { familyRegistration, parent },
    services: { tracker }
  } = useDependencies();
  const cookies = new Cookies();
  const navigate = useNavigate();
  const { t } = useSharedTranslation(namespaces.family);

  const [dirty, setDirty] = useState(false);
  const [zipConfirmed, setZipConfirmed] = useState(!!familyRegistration.profile.address?.zip);

  familyRegistration.setCurrentRoute(Routes.parent.registration.location);

  const loadAnonymousUserData = async (): Promise<void> => {
    if (familyRegistration.profile.address?.zip) return;

    const usUuid = localStorage.getItem(Storage.UUID);
    if (!usUuid) return;

    const response = await AnonymousUserService.findByUuid(usUuid);
    const anonymousUser = response.data?.data;

    if (anonymousUser?.zip) {
      familyRegistration.setZipCode(anonymousUser.zip);

      const cityResponse = await CityService.getByFilter({
        params: { zipCode: anonymousUser.zip }
      });

      if (cityResponse.data?.data?.length) {
        const city = cityResponse.data.data[0];
        familyRegistration.setCity(city.name!);
        familyRegistration.setState(city.state!);
        setZipConfirmed(true);
      }
    }
  };

  useEffect(() => {
    if (cookies.get(Storage.JUST_BROWSING)) {
      familyRegistration.setJustBrowsing(true);
    }

    loadAnonymousUserData();
  }, []);

  const updateParentAddress = async (): Promise<void> => {
    try {
      const params = {
        id: familyRegistration.profile.id,
        address: familyRegistration.profile.address,
      };

      if (familyRegistration.profile.id) {
        const updatedParent = (await ParentService.patch(familyRegistration.profile.id, params)).data;
        parent.updateParent(updatedParent.data);
      }
    } catch {
      toast.error(t('pages.nux.family.locationErrors.errorUpdatingAddress'));
    }
  }

  const createSearch = async (): Promise<void> => {
    try {
      await createSavedSearch(familyRegistration);
    } catch {
      toast.error(t('pages.nux.family.locationErrors.errorSavingSearch'));
    }
  };

  const deleteSearch = async (): Promise<void> => {
    try {
      if (familyRegistration.savedSearchId) {
        await SavedSearchService.delete(familyRegistration.savedSearchId);
      }

      familyRegistration.setSavedSearchId(undefined);
      familyRegistration.setCreatedOn(undefined);
    } catch {
      toast.error(t('pages.nux.family.locationErrors.errorDeletingSearch'));
    }
  };

  const handleContinue = (): void => {
    void (async (): Promise<void> => {
      await updateParentAddress();
      if ((familyRegistration.availabilityType === JobAvailabilityType.partTime || familyRegistration.availabilityType === JobAvailabilityType.fullTime) && !familyRegistration.justBrowsing) {
        createSearch();
      } else if (familyRegistration.savedSearchId) {
        deleteSearch();
      }

      if (familyRegistration.browsingOrAbandoned()) {
        navigate(Routes.parent.registration.success);
        return;
      }
      navigate(Routes.parent.registration.great);
    })();
  }

  const handleNeighborhoodChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDirty(true);
    familyRegistration.setNeighborhood(event.target.value);
  }

  const handleZipCodeChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const zipCode = event.target.value;
    familyRegistration.setZipCode(zipCode);

    if (ValidationService.isValidZip(zipCode)) {
      try {
        const response = (await CityService.getByFilter({ params: { zipCode: event.target.value } })).data;

        if (response.data.length) {
          setZipConfirmed(true);
          const city = response.data[0];
          familyRegistration.setCity(city.name!);
          familyRegistration.setState(city.state!);
        } else {
          setZipConfirmed(false);
          familyRegistration.setCity('');
          familyRegistration.setState('');
        }
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.data.errors) {
          toast.error(e.response.data.errors[0].message);
        } else {
          console.warn(e);
        }
      } finally {
        setDirty(true);
      }
    }
  }

  const handleStateChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDirty(true);
    familyRegistration.setState(event.target.value);
  }

  const isValidStateCode = states.some((state: { code: string | undefined; }) => state.code === familyRegistration.profile.address?.state?.toUpperCase());

  const isValidZipCode = familyRegistration.profile.address && familyRegistration.profile.address.zip?.length === 5 && zipConfirmed;

  const isContinueDisabled = !familyRegistration.profile.address?.city || !isValidZipCode || !isValidStateCode;

  const renderInfoCard = (): JSX.Element | undefined => ((isValidStateCode && isValidZipCode) || !dirty) ? (
    <InfoCard
      iconName={faMapLocation}
      title={t('pages.nux.family.location.infoCard.title')}
      description={t('pages.nux.family.location.infoCard.description')}
    />
  ) : undefined;

  const getTitle = (): string => {
    if (familyRegistration.justBrowsing) {
      return t('pages.nux.family.location.titleJustBrowsing');
    }
    return t('pages.nux.family.location.title');
  }

  return (
    <NuxContainer onContinue={handleContinue} isContinueDisabled={isContinueDisabled} infoCard={renderInfoCard()}
                  title={getTitle()} familyRegistration={familyRegistration}
                  tracker={tracker} navigate={navigate}>
      <div className='flex flex-col w-[90%] md:w-[80%] lg:w-[70%] md:pb-2 lg:pb-6'>
        <Input
          id='location-zip'
          className='flex-1 mr-6 pb-2'
          label={t('common.zipCode')}
          maxLength={5}
          type='text'
          onChange={handleZipCodeChange}
          value={familyRegistration.profile.address?.zip || ''}
        />
        <Alert className='w-full' color='error' isOpen={!isValidZipCode && dirty}>
          {t('validation.errors.invalidZip')}
        </Alert>
        <Input
          id='location-city'
          className='flex-1 mr-6 pb-2'
          label={t('pages.nux.family.location.city')}
          maxLength={50}
          type='text'
          onChange={(event: ChangeEvent<HTMLInputElement>): void => familyRegistration.setCity(event.target.value)}
          value={familyRegistration.profile.address?.city || ''}
        />
        <Input
          id='location-state'
          className='flex-1 mr-6 pb-2'
          label={t('pages.nux.family.location.state')}
          maxLength={2}
          type='text'
          onChange={handleStateChange}
          value={familyRegistration.profile.address?.state || ''}
        />
        <Input
          id='location-neighborhood'
          className='flex-1 mr-6'
          label={t('pages.nux.family.location.neighborhood')}
          maxLength={50}
          type='text'
          onChange={handleNeighborhoodChange}
          value={familyRegistration.profile.address?.neighborhood || ''}
        />
        <Alert className='w-full' color='error' isOpen={!isValidStateCode && dirty}>
          {t('validation.errors.invalidState')}
        </Alert>
      </div>
    </NuxContainer>
  )
});
